import * as d3 from "d3";
import * as d3Zoom from "d3-zoom";

import React, { useEffect, useRef } from "react";

export default function Heatmap({ idx_pt, distance_matrix }) {
  const names = [idx_pt].concat(
    Object.entries(distance_matrix[idx_pt])
      .sort((a, b) => a[1] - b[1])
      .map((item) => item[0])
  );

  const svgRef = useRef(null);

  useEffect(() => {
    // Clear existing heatmap elements
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Define your dimensions
    const margin = { top: 50, right: 50, bottom: 50, left: 50 };
    const width = 500 - margin.left - margin.right;
    const height = 500 - margin.top - margin.bottom;

    // Create the SVG container
    const container = d3
      .select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left + 35},${margin.top + 35})`);

    // Define your data and scales
    const rows = names;
    const columns = names;
    const colorScale = d3.scaleSequential(d3.interpolateYlOrRd).domain([0, 4]); // Adjust the domain as needed
    const numPatients = rows.length; // Number of patients

    const xScale = d3.scaleBand().domain(columns).range([0, width]).padding(0);

    const yScale = d3.scaleBand().domain(rows).range([0, height]).padding(0);

    // Create the heatmap

    container
      .selectAll(".cell")
      .data(rows, (row) => row)
      .enter()
      .append("g")
      .selectAll(".cell")
      .data((row) =>
        columns.map((col) => ({ row, col, value: distance_matrix[row][col] }))
      )
      .enter()
      .append("rect")
      .attr("x", (d) => xScale(d.col))
      .attr("y", (d) => yScale(d.row))
      .attr("width", xScale.bandwidth())
      .attr("height", yScale.bandwidth())
      .style("fill", (d) => colorScale(d.value));

    container
      .selectAll(".cell")
      .data(rows, (row) => row)
      .enter()
      .append("g")
      .selectAll(".cell")
      .data((row) =>
        columns.map((col) => ({ row, col, value: distance_matrix[row][col] }))
      )
      .enter()
      .append("text")
      .attr("x", (d) => xScale(d.col) + xScale.bandwidth() / 2)
      .attr("y", (d) => yScale(d.row) + yScale.bandwidth() / 2)
      .attr("dy", "0.35em") // Adjust the vertical alignment as needed
      .style("text-anchor", "middle")
      .style("fill", "black") // Adjust the text color as needed
      .text((d) => d.value);

    container
      .append("g")
      .selectAll(".x-axis-label")
      .data(columns)
      .enter()
      .append("text")
      .attr("class", "x-axis-label")
      .attr("x", -height + 410)
      .attr("y", (d, i) => xScale(d) + xScale.bandwidth() / 1.3)
      .style("text-anchor", "start")
      .style("font-size", "0.85rem")
      .attr("transform", "rotate(-90)")
      .text((d) => d);

    container
      .append("g")
      .selectAll(".y-axis-label")
      .data(rows)
      .enter()
      .append("text")
      .attr("class", "y-axis-label")
      .attr("x", -10) // Adjust the position as needed
      .attr("y", (d) => yScale(d) + yScale.bandwidth() / 1.25)
      .style("font-size", "0.85rem")
      .style("text-anchor", "end")
      .text((d) => d);

    const zoom = d3Zoom
      .zoom()
      .scaleExtent([0.1, 10]) // Set the zoom scale limits
      .on("zoom", (event) => {
        container.selectAll("g").attr("transform", event.transform);
      });

    container.call(zoom);

    // Add row and column labels if needed
    // You can add text elements here based on the 'rows' and 'columns' arrays
  }, [distance_matrix]);

  return <svg ref={svgRef}></svg>;
}
